import { useState } from 'react';
import { useOne, useGo, useCustomMutation, useInvalidate } from '@refinedev/core';
import { useParams } from 'react-router-dom';
import {
  Loader2,
  Info,
  Settings,
  Calendar,
  Clock,
  User as UserIcon,
  CreditCard,
  ShoppingCart,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormHeader } from '@/components/forms';
import {
  EntityAnalyticsCard,
  EntityAnalyticsGrid,
  TwoColumnLayout,
  TabsWithPersistence,
} from '@/components/shared';
import { FamilyMembersSection } from './components';
import type { Subscription } from './types';

const STATUS_BADGE_MAP: Record<string, { label: string; className: string }> = {
  active: { label: 'Активна', className: 'bg-green-500/10 text-green-600 border-green-500/20' },
  pending: { label: 'Ожидает', className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20' },
  expired: { label: 'Истекла', className: 'bg-gray-500/10 text-gray-500 border-gray-500/20' },
  cancelled: { label: 'Отменена', className: 'bg-red-500/10 text-red-600 border-red-500/20' },
};

const STATUS_TRANSITIONS: Record<string, string[]> = {
  pending: ['active', 'cancelled'],
  active: ['expired', 'cancelled'],
  expired: ['active'],
  cancelled: [],
};

function getStatusBadge(status: string) {
  const s = status.toLowerCase();
  const badge = STATUS_BADGE_MAP[s] ?? { label: status, className: '' };
  return (
    <Badge variant="outline" className={badge.className}>
      {badge.label}
    </Badge>
  );
}

function formatDate(value: string | Date | null | undefined): string {
  if (!value) {return '\u2014';}
  return new Date(value).toLocaleDateString('ru-RU');
}

function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) {return '\u2014';}
  return new Date(value).toLocaleString('ru-RU');
}

function getRemainingDays(endDate: string | null | undefined): string {
  if (!endDate) {return 'Бессрочная';}
  const end = new Date(endDate);
  const now = new Date();
  const diffMs = end.getTime() - now.getTime();
  if (diffMs <= 0) {return 'Истекла';}
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (days === 1) {return '1 день';}
  if (days >= 2 && days <= 4) {return `${days} дня`;}
  return `${days} дней`;
}

function getUserName(sub: Subscription): string {
  if (!sub.user) {return `#${sub.userId}`;}
  const name = [sub.user.firstName, sub.user.lastName].filter(Boolean).join(' ');
  return name || (sub.user.username ? `@${sub.user.username}` : `#${sub.userId}`);
}

// -- Overview Tab --

function OverviewTab({ subscription: sub }: { subscription: Subscription }) {
  const go = useGo();
  const status = String(sub.status).toLowerCase();
  const remaining = getRemainingDays(sub.endDate);

  const mainContent = (
    <div className="space-y-6">
      {/* Пользователь */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <UserIcon className="h-4 w-4" />
            Пользователь
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Имя</span>
              <p className="font-medium">{getUserName(sub)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Username</span>
              <p className="font-medium">{sub.user?.username ? `@${sub.user.username}` : '\u2014'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Telegram ID</span>
              <p className="font-medium font-mono">{sub.user?.telegramId ?? '\u2014'}</p>
            </div>
            <div>
              <Button
                variant="link"
                className="h-auto p-0"
                onClick={() => go({ to: `/users/show/${sub.userId}` })}
              >
                Перейти к пользователю
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* План подписки */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <CreditCard className="h-4 w-4" />
            План подписки
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Код</span>
              <p className="font-medium font-mono">{sub.subscriptionPlan?.code ?? sub.plan?.code ?? '\u2014'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Длительность</span>
              <p className="font-medium">
                {sub.subscriptionPlan?.durationDays !== null && sub.subscriptionPlan?.durationDays !== undefined
                  ? `${sub.subscriptionPlan.durationDays} дней`
                  : (sub.plan?.durationDays !== null && sub.plan?.durationDays !== undefined
                    ? `${sub.plan.durationDays} дней`
                    : 'Бессрочно')}
              </p>
            </div>
            <div>
              <Button
                variant="link"
                className="h-auto p-0"
                onClick={() => {
                  const planId = sub.subscriptionPlanId ?? sub.planId;
                  if (planId) {go({ to: `/subscription-plans/show/${planId}` });}
                }}
              >
                Перейти к плану
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Семейная подписка */}
      {sub.isFamilyOwner && <FamilyMembersSection subscription={sub} />}
    </div>
  );

  const sidebarContent = (
    <div className="space-y-6">
      {/* Источник */}
      {(sub.sourceOrderId || sub.sourcePaymentId) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShoppingCart className="h-4 w-4" />
              Источник
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-sm">
              {sub.sourceOrderId && (
                <div>
                  <span className="text-muted-foreground">Заказ</span>
                  <p className="font-medium">
                    <Button
                      variant="link"
                      className="h-auto p-0"
                      onClick={() => go({ to: `/orders/show/${sub.sourceOrderId}` })}
                    >
                      #{sub.sourceOrderId}
                    </Button>
                  </p>
                </div>
              )}
              {sub.sourcePaymentId && (
                <div>
                  <span className="text-muted-foreground">Платёж</span>
                  <p className="font-medium">
                    <Button
                      variant="link"
                      className="h-auto p-0"
                      onClick={() => go({ to: `/payments/show/${sub.sourcePaymentId}` })}
                    >
                      #{sub.sourcePaymentId}
                    </Button>
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Метаданные */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Метаданные</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm">
            <div>
              <span className="text-muted-foreground">ID</span>
              <p className="font-medium font-mono">{sub.id}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Создана</span>
              <p className="font-medium">{formatDateTime(sub.createdAt)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Обновлена</span>
              <p className="font-medium">{formatDateTime(sub.updatedAt)}</p>
            </div>
            {sub.cancelledAt && (
              <div>
                <span className="text-muted-foreground">Отменена</span>
                <p className="font-medium">{formatDateTime(sub.cancelledAt)}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Аналитические карточки */}
      <EntityAnalyticsGrid>
        <EntityAnalyticsCard
          title="Статус"
          value={STATUS_BADGE_MAP[status]?.label ?? String(sub.status)}
          icon={getStatusBadge(status)}
        />
        <EntityAnalyticsCard
          title="Дата начала"
          value={formatDate(sub.startDate)}
          icon={<Calendar className="h-4 w-4" />}
        />
        <EntityAnalyticsCard
          title="Дата окончания"
          value={sub.endDate ? formatDate(sub.endDate) : 'Бессрочно'}
          icon={<Calendar className="h-4 w-4" />}
        />
        <EntityAnalyticsCard
          title="Оставшиеся дни"
          value={remaining}
          icon={<Clock className="h-4 w-4" />}
        />
      </EntityAnalyticsGrid>

      <TwoColumnLayout main={mainContent} sidebar={sidebarContent} />
    </div>
  );
}

// -- Manage Tab --

function ManageTab({ subscription: sub }: { subscription: Subscription }) {
  const invalidate = useInvalidate();
  const { mutate: updateSubscription, mutation: updateMutation } = useCustomMutation();
  const { mutate: extendSubscription, mutation: extendMutation } = useCustomMutation();

  const currentStatus = String(sub.status).toLowerCase();
  const allowedTransitions = STATUS_TRANSITIONS[currentStatus] ?? [];

  const [newStatus, setNewStatus] = useState('');
  const [extendDays, setExtendDays] = useState('');

  const isUpdating = updateMutation.isPending;
  const isExtending = extendMutation.isPending;

  const handleStatusChange = () => {
    if (!newStatus) {return;}
    updateSubscription(
      {
        url: `/subscriptions/${sub.id}`,
        method: 'put',
        values: { status: newStatus.toUpperCase() },
      },
      {
        onSuccess: () => {
          setNewStatus('');
          invalidate({ resource: 'subscriptions', invalidates: ['detail'] });
        },
      },
    );
  };

  const handleExtend = () => {
    const days = parseInt(extendDays, 10);
    if (isNaN(days) || days <= 0) {return;}
    extendSubscription(
      {
        url: `/subscriptions/${sub.id}/extend`,
        method: 'post',
        values: { days },
      },
      {
        onSuccess: () => {
          setExtendDays('');
          invalidate({ resource: 'subscriptions', invalidates: ['detail'] });
        },
      },
    );
  };

  const mainContent = (
    <div className="space-y-6">
      {/* Смена статуса */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Смена статуса</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {allowedTransitions.length > 0 ? (
            <>
              <div className="space-y-2">
                <Label>Новый статус</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите статус" />
                  </SelectTrigger>
                  <SelectContent>
                    {allowedTransitions.map((s) => (
                      <SelectItem key={s} value={s}>
                        {STATUS_BADGE_MAP[s]?.label ?? s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handleStatusChange}
                disabled={!newStatus || isUpdating}
              >
                {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Применить
              </Button>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Нет допустимых переходов из текущего статуса
            </p>
          )}
        </CardContent>
      </Card>

      {/* Продление */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Продление подписки</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Количество дней</Label>
            <Input
              type="number"
              min="1"
              placeholder="Например: 30"
              value={extendDays}
              onChange={(e) => setExtendDays(e.target.value)}
            />
          </div>
          <Button
            onClick={handleExtend}
            disabled={!extendDays || parseInt(extendDays, 10) <= 0 || isExtending}
          >
            {isExtending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Продлить
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  const sidebarContent = (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Текущее состояние</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <span className="text-muted-foreground">Статус</span>
            <div className="mt-1">{getStatusBadge(currentStatus)}</div>
          </div>
          <div>
            <span className="text-muted-foreground">Дата начала</span>
            <p className="font-medium">{formatDate(sub.startDate)}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Дата окончания</span>
            <p className="font-medium">{sub.endDate ? formatDate(sub.endDate) : 'Бессрочно'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">План</span>
            <p className="font-medium font-mono">
              {sub.subscriptionPlan?.code ?? sub.plan?.code ?? '\u2014'}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return <TwoColumnLayout main={mainContent} sidebar={sidebarContent} />;
}

// -- Main Show Page --

export function SubscriptionsShow() {
  const go = useGo();
  const { id } = useParams<{ id: string }>();

  const subResult = useOne<Subscription>({
    resource: 'subscriptions',
    id: id!,
  });

  const sub = subResult.result;
  const isLoading = subResult.query.isLoading;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!sub) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Подписка не найдена</p>
        <Button variant="link" onClick={() => go({ to: '/subscriptions' })}>
          Вернуться к списку
        </Button>
      </div>
    );
  }

  const status = String(sub.status).toLowerCase();

  return (
    <div className="space-y-6">
      <FormHeader
        title={`Подписка #${sub.id}`}
        onBack={() => go({ to: '/subscriptions' })}
        titleAddon={getStatusBadge(status)}
        sticky
      />

      <TabsWithPersistence
        tabs={[
          {
            value: 'overview',
            label: 'Обзор',
            icon: <Info className="h-4 w-4" />,
            content: <OverviewTab subscription={sub} />,
          },
          {
            value: 'manage',
            label: 'Управление',
            icon: <Settings className="h-4 w-4" />,
            content: <ManageTab subscription={sub} />,
          },
        ]}
        defaultTab="overview"
      />
    </div>
  );
}
